import { Button, Image, Menu, message, Popover, Row } from 'antd'
import type { MenuProps } from 'antd'
import {
  BarChartOutlined,
  CheckSquareOutlined,
  CloseOutlined,
  FileSearchOutlined,
  FolderOpenOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SettingOutlined,
  TagOutlined,
} from '@ant-design/icons'
import { memo, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/providers/auth.provider'
import LOGO from '@/assets/images/icon.png'
import Sider from 'antd/lib/layout/Sider'
import Permission from '@/components/Permission'
import HttpRequest from '@/config/axios.config'
import { useQuery } from 'react-query'
import { USER_ROLE } from '@/constant/user'
import { DEFAULT_ROUTE } from '@/constant/common'
import { orderBy } from 'lodash'
import { isEmptyValue } from '@/utils/common'
import i18n from '@/config/translation'
import { useMobileSidebar } from './MobileSidebarContext'
import { useCompanySettingShowModules } from '@/hooks/useCompanySettingShowModules'
import { toggleDebugConsole } from '@/utils/debugConsole'

const { ADMIN, STANDARD, SUPPLIER, INSPECTOR } = USER_ROLE
const DEBUG_CONSOLE_CLICK_COUNT = 5
const DEBUG_CONSOLE_CLICK_WINDOW_MS = 3000

interface SideBarContentProps {
  collapsed: boolean
  onToggleCollapse?: () => void
  showCollapseToggle?: boolean
  onNavigateComplete?: () => void
}

export function SideBarContent({
  collapsed,
  onToggleCollapse,
  showCollapseToggle = true,
  onNavigateComplete,
}: SideBarContentProps) {
  const route = useLocation()

  const queriesCacheTime = 3600000

  const navigate = useNavigate()
  const { user } = useAuth()
  const { isMobile, closeMobileSidebar } = useMobileSidebar()
  // Tolerate the varying /users/profile shapes (singular `subscription`, top-level
  // `role_access`, or plural `subscriptions[]`) — see ProductPermission.ts. If only the
  // singular path is checked, a plural-shaped payload leaves userRole undefined, which
  // hides every permission-gated menu item (e.g. "My Orders").
  const userRole =
    user?.subscription?.role_access?.role ??
    user?.role_access?.role ??
    user?.subscriptions?.[0]?.role_access?.role
  const isSupplierRole = userRole === SUPPLIER

  const [selectMenu, setSelectMenu] = useState('order')
  const debugConsoleClicksRef = useRef<number[]>([])

  const { data: menus } = useQuery(
    ['extra_menus', user?.id],
    async () => {
      const response: any = await HttpRequest.get(
        `/company-settings?type=time-action&status=active&name=extra_menus`,
        {
          params: { company_id: user.company.id },
        }
      )
      return response?.data?.data[0]?.value || { task_list: { status: false } }
    },
    {
      cacheTime: queriesCacheTime,
    }
  )

  const { data: helpSupport } = useQuery('help_support', async () => {
    const response: any = await HttpRequest.get(`/company-settings/system`, {
      params: { status: 'active', name: 'help_support', type: 'general' },
    })
    return response?.data?.data[0]?.value || null
  })

  const { shouldShowProductModule } = useCompanySettingShowModules(user?.id, {
    cacheTime: queriesCacheTime,
  })

  const onNavigate = (routes: string) => {
    navigate(routes)
  }

  const handleTaskRoute = () => {
    const isMatchPath = route.pathname === '/tasks'
    const queryParams = new URLSearchParams(window.location.search)
    const taskRoute = `${DEFAULT_ROUTE.TASKS}${
      user?.id ? `&assignees=${user.id}` : ''
    }`
    const getRoute = isMatchPath
      ? `/tasks?${queryParams.toString()}`
      : taskRoute

    onClickMenu({ route: getRoute })
  }

  const handleOrderRoute = () => {
    const isMatchPath = route.pathname === '/pbf'
    const queryParams = new URLSearchParams(window.location.search)
    const getRoute = isMatchPath
      ? `/pbf?${queryParams.toString()}`
      : DEFAULT_ROUTE.PBF_ORDERS

    onClickMenu({ route: getRoute })
  }

  const handleProductRoute = () => {
    const isMatchPath = route.pathname === '/products'
    const queryParams = new URLSearchParams(window.location.search)
    const getRoute = isMatchPath
      ? `/products?${queryParams.toString()}`
      : DEFAULT_ROUTE.PRODUCTS

    onClickMenu({ route: getRoute })
  }

  const handleInspectionRoute = () => {
    const isMatchPath = route.pathname === '/qsf/inspection'
    const queryParams = new URLSearchParams(window.location.search)
    const getRoute = isMatchPath
      ? `/qsf/inspection?${queryParams.toString()}`
      : DEFAULT_ROUTE.QSF_INSPECTION

    onClickMenu({ route: getRoute })
  }

  useEffect(() => {
    const path: any = route.pathname.split('/')[1]
    switch (path) {
      case 'qsf':
        setSelectMenu('inspection')
        break
      case 'pbf':
        setSelectMenu('order')
        break
      case 'liveboard':
        setSelectMenu('liveboard')
        break
      case 'tasks':
        setSelectMenu('tasks')
        break
      case 'products':
        setSelectMenu('product')
        break
      default:
        setSelectMenu('')
        break
    }
  }, [route.pathname])

  const onClickCompanyLogo = async () => {
    const now = Date.now()
    const recentClicks = debugConsoleClicksRef.current.filter(
      (clickedAt) => now - clickedAt <= DEBUG_CONSOLE_CLICK_WINDOW_MS
    )

    recentClicks.push(now)
    debugConsoleClicksRef.current = recentClicks

    if (recentClicks.length < DEBUG_CONSOLE_CLICK_COUNT) return

    debugConsoleClicksRef.current = []

    try {
      const isEnabled = await toggleDebugConsole()
      message.success(`Debug console ${isEnabled ? 'enabled' : 'disabled'}`, 3)
    } catch {
      message.error('Debug console could not be enabled', 3)
    }
  }

  const companyLogo = (
    <div className="logo-container">
      <Row align="middle" onClick={onClickCompanyLogo}>
        <Image
          src={user?.company?.logo?.url_path ?? ''}
          fallback={LOGO}
          preview={false}
          alt="LOGO"
        />

        {!collapsed && (
          <div>
            <p className="company-name">{user?.company?.name}</p>
            {user?.suppliers?.[0]?.name &&
              user?.subscription?.role_access?.role === SUPPLIER && (
                <p className="supplier-name">{user?.suppliers?.[0]?.name}</p>
              )}
          </div>
        )}
      </Row>
      {isMobile && (
        <Button
          aria-label="Close sidebar"
          className="mobile-sidebar-close"
          icon={<CloseOutlined />}
          onClick={closeMobileSidebar}
          type="text"
        />
      )}
    </div>
  )

  const onClickMenu = ({ route }) => {
    onNavigate(route)
    onNavigateComplete?.()
  }

  const shouldShowOnlyProductOnMobile = isMobile && isSupplierRole

  const canAccess = (roles: Array<string>) =>
    !!userRole && roles.includes(userRole)

  // Build the sidebar as a flat `items` array (antd's recommended API) instead of wrapping
  // <Menu.Item>s in <Permission> inside a <Fragment> as <Menu> children. rc-menu reads the
  // React key from those wrapper elements (which carry none), so multiple items collided on
  // key `undefined` and rc-menu intermittently dropped one of them — that was the
  // "My Orders sometimes missing" bug. Computing visibility synchronously here keeps every
  // item's key unique and stable, so nothing gets de-duplicated away.
  const menuItems: MenuProps['items'] = [
    !shouldShowOnlyProductOnMobile &&
    menus?.task_list?.status &&
    canAccess([ADMIN, STANDARD, SUPPLIER])
      ? {
          key: 'tasks',
          title: 'Tasks',
          className: route.pathname.includes('/tasks')
            ? 'active-menu-item'
            : '',
          onClick: () => handleTaskRoute(),
          icon: (
            <div className="icon-container task-background">
              <CheckSquareOutlined className="icon" />
            </div>
          ),
          label: 'My Tasks',
        }
      : null,
    shouldShowProductModule && canAccess([ADMIN, STANDARD, SUPPLIER])
      ? {
          key: 'product',
          title: 'Products',
          className: route.pathname.includes('/product')
            ? 'active-menu-item'
            : '',
          onClick: () => handleProductRoute(),
          icon: (
            <div className="icon-container product-background">
              <TagOutlined className="icon" />
            </div>
          ),
          label: 'My Products',
        }
      : null,
    !shouldShowOnlyProductOnMobile && canAccess([ADMIN, STANDARD, SUPPLIER, INSPECTOR])
      ? {
          key: 'order',
          title: 'Order',
          className: route.pathname.includes('/pbf') ? 'active-menu-item' : '',
          onClick: () => handleOrderRoute(),
          icon: (
            <div className="icon-container order-background">
              <FolderOpenOutlined className="icon" />
            </div>
          ),
          label: `My ${i18n.t('orders')}`,
        }
      : null,
    !shouldShowOnlyProductOnMobile
      ? {
          key: 'inspection',
          className: route.pathname.includes('/qsf/inspection')
            ? 'active-menu-item'
            : '',
          onClick: () => handleInspectionRoute(),
          icon: (
            <div className="icon-container inspection-background">
              <FileSearchOutlined className="icon" />
            </div>
          ),
          label: 'My Quality',
        }
      : null,
    !shouldShowOnlyProductOnMobile && canAccess([ADMIN])
      ? {
          key: 'liveboard',
          className: route.pathname.includes('/liveboard')
            ? 'active-menu-item'
            : '',
          onClick: () => onClickMenu({ route: '/liveboard/qsf' }),
          icon: (
            <div className="icon-container">
              <BarChartOutlined className="icon" />
            </div>
          ),
          label: 'My Liveboard',
        }
      : null,
  ]

  function HelpAndSupportPopoverContent() {
    return (
      <div className="help-support-content">
        {orderBy(helpSupport?.menus, 'display_order')?.map((item, index) => {
          return (
            <Button
              key={index}
              type="text"
              size="large"
              onClick={() => openUrl(item.url)}
            >
              <img src={item?.image} className="help-icon" />
              {item?.title}
            </Button>
          )
        })}
      </div>
    )
  }

  const tabsRef = useRef({})

  const openUrl = (url: string): void => {
    if (!tabsRef.current[url] || tabsRef.current[url].closed) {
      tabsRef.current[url] = window.open(url, '_blank')
    } else {
      tabsRef.current[url].focus()
    }
  }

  return (
    <div className="side-bar-nav">
      <div className="header-nav">
        {companyLogo}
        <Menu selectedKeys={[selectMenu]} mode="inline" items={menuItems} />
      </div>

      {!shouldShowOnlyProductOnMobile && (
        <div className="footer-nav">
          <Permission roles={[ADMIN]}>
            <div
              className="botton-container"
              onClick={() => onClickMenu({ route: '/system-configure' })}
            >
              <Button
                className="botton-setting"
                icon={<SettingOutlined className="icon" />}
              />
              {!collapsed && <p>Settings</p>}
            </div>
          </Permission>
          {!isEmptyValue(helpSupport) && (
            <Popover
              showArrow={false}
              content={HelpAndSupportPopoverContent}
              trigger={['focus', 'click']}
              placement="right"
              getPopupContainer={(trigger) => trigger.parentElement}
              overlayInnerStyle={{
                marginLeft: collapsed ? 0 : 19,
              }}
            >
              <div className="botton-container">
                <Button
                  className="botton-setting"
                  icon={
                    <img
                      src={helpSupport?.image}
                      className="help-support-main-icon"
                    />
                  }
                />

                {!collapsed && (
                  <div>
                    <p>{helpSupport?.title}</p>
                  </div>
                )}
              </div>
            </Popover>
          )}

          {showCollapseToggle && (
            <div className="botton-container">
              <Button
                className="botton-show-hide"
                type="ghost"
                icon={
                  collapsed ? (
                    <MenuUnfoldOutlined className="icon" />
                  ) : (
                    <MenuFoldOutlined className="icon" />
                  )
                }
                size="large"
                onClick={onToggleCollapse}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function SideBar() {
  const [collapsed, setCollapsed] = useState(true)

  return (
    <Sider
      width={240}
      collapsedWidth={88}
      collapsed={collapsed}
      style={{ zIndex: 26 }}
      className="desktop-sidebar"
    >
      <div className="sidebar-shell desktop-sidebar-shell">
        <SideBarContent
          collapsed={collapsed}
          onToggleCollapse={() => setCollapsed(!collapsed)}
        />
      </div>
    </Sider>
  )
}

export default memo(SideBar)
